
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { Validate } from '@/types';
import { formatValue } from '@/utils/format';
import { CATERING_OPEN_ITEM } from '@/utils/constants';
import Toast from '@/utils/toast';
import StandardModal from '@/components/shared/suites/StandardModal.vue';
import TextField from '@/components/shared/TextField.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import QuantityInput from '@/components/shared/suites/QuantityInput.vue';
import TaxOverwrite from '@/components/item/TaxOverwrite.vue';
import SelectComponent from '@/components/shared/Select.vue';
import RichTextEditor from '@/components/shared/RichTextEditor.vue';

const namespace: string = 'auth';

@Component<OpenItemModal>({
	components: {
		StandardModal,
		TextField,
		TextButton,
		QuantityInput,
		TaxOverwrite,
		SelectComponent,
		RichTextEditor,
		ValidationObserver,
		ValidationProvider
	}
})
export default class OpenItemModal extends Vue {
	@Prop({ type: Boolean, default: false }) private showModal!: boolean;
	@Action('addOpenItemToCart', { namespace: 'orders' }) private addOpenItemToCart!: (payload: { item: MenuItem; quantity: number; saveOpenItem: boolean; }) => Promise<void>;
	@Getter('getRestaurant', { namespace }) private restaurant!: Restaurant;
	@Getter('isPosIntegrated', { namespace }) private isPosIntegrated!: boolean;
	@Getter('showReportingCategory', { namespace }) private showReportingCategory!: boolean;
	@Getter('getPriceAffixes', { namespace }) private priceAffixes!: PriceAffixes;
	@Getter('getMenus', { namespace: 'menus' }) private menus!: Menu[];

	private selectedItem: MenuItem | string = '';
	private price: string = '';
	private description: string = '';
	private category: string = '';
	private menu: Menu | null = null;
	private section: MenuSection | null = null;
	private taxRate: Taxes | null = null;
	private quantity: number = 1;
	private saveOpenItem: boolean = false;
	private loading: boolean = false;

	$refs!: { observer: Validate };

	private get items(): MenuItem[] {
		return this.menus.flatMap((m: Menu) => {
			if (m.sections?.length) {
				return m.sections.flatMap((s: MenuSection) => {
					return (s.items || []).map((i: MenuItem) => {
						return { ...i, menu_id: m.id ?? i.menu_id, section_id: s.id ?? i.section_id };
					});
				});
			}
			return m.items || [];
		});
	}

	private get isExistingItem(): boolean {
		return typeof this.selectedItem === 'object' && this.selectedItem !== null;
	}

	private get itemName(): string {
		if (this.isExistingItem) {
			return (this.selectedItem as MenuItem).name;
		}
		return this.selectedItem as string;
	}

	private get operatorMenus(): Menu[] {
		const menus = [];
		const uncategorizedMenu = this.menus.find(m => m.name === CATERING_OPEN_ITEM.MENU_NAME);
		if (uncategorizedMenu) {
			menus.push(uncategorizedMenu);
		}
		else {
			// If uncategorized menu doesn't exist, add a placeholder menu and section
			// These will both be created by Remy when they don't exist
			menus.push({
				name: this.$t('orders.open_item.uncategorized_menu'),
				allow_sections: true,
				sections: [{
					name: this.$t('orders.open_item.uncategorized_section')
				}]
			} as Menu);
		}
		menus.push(...this.menus.filter((m: Menu) => m.access_level === 'operator' && m.name !== CATERING_OPEN_ITEM.MENU_NAME));
		return menus;
	}

	private created(): void {
		this.setDefaultMenuAndSection();
	}

	/**
	 * Pre-fill price, description, tax rate and accounting category when an existing item is selected
	 * If selected item is part of the uncategorized or operator only menu then set the item's menu and section as well
	 *
	 * @param {MenuItem | string} value
	 * @return {void}
	 */
	private onItemSelected(value: MenuItem | string): void {
		if (typeof value === 'object' && value !== null) {
			this.price = String(value.price);
			this.description = value.description;
			this.taxRate = value.tax_rate || null;
			this.category = value.category || '';

			if (value.menu_id) {
				const itemMenu = this.menus.find(m => m.id === value.menu_id && (m.access_level === 'operator' || m.name === CATERING_OPEN_ITEM.MENU_NAME));
				if (itemMenu) {
					this.menu = itemMenu;
					this.onMenuSelected(itemMenu);
					const itemSection = itemMenu.sections?.find((s: MenuSection) => s.id === value.section_id);
					if (itemSection) {
						this.section = itemSection;
					}
				}
			}
		}
	}

	/**
	 * Get the name of the menu an item belongs to, used to tell apart items with the same name in the dropdown
	 *
	 * @param {number} menuId
	 * @return {string}
	 */
	private getItemMenuName(menuId: number): string {
		return this.menus.find((m: Menu) => m.id === menuId)?.name || '';
	}

	/**
	 * Build a unique value for each item in the dropdown.
	 * Vuetify de-duplicates the combobox items by their value, which falls back to the item's name when no item-value is provided
	 *
	 * @param {MenuItem} item
	 * @return {string}
	 */
	private getItemUniqueValue(item: MenuItem): string {
		return `${item.menu_id}-${item.section_id ?? 'menu'}-${item.id}`;
	}

	/**
	 * Reset section to null on menu select
	 * if there is only one section in the menu select it
	 *
	* @param {Menu|null} item
	* @return {void}
	 */
	private onMenuSelected(menu: Menu | null): void {
		this.section = null;
		if (menu?.sections?.length === 1) {
			this.section = menu.sections[0];
		}
	}

	/**
	 * Build the error messages for the menu select, including an error when the selected menu has no sections
	 *
	 * @param {string[]} errors
	 * @return {string | string[]}
	 */
	private getMenuErrors(errors: string[]): string | string[] {
		if (this.menu?.allow_sections && !this.menu.sections?.length) {
			return this.$t('orders.open_item.menu_no_sections');
		}
		return errors ? errors[0] : [];
	}

	/**
	 * Validate the form, add the open item to the cart and refresh menus
	 *
	 * @return {Promise<void>}
	 */
	private async addToOrder(): Promise<void> {
		const isValid = await this.$refs.observer.validate();
		if (!isValid) {
			return;
		}

		this.loading = true;

		try {
			const formattedPrice = String(this.formatItemPrice(+this.price));
			const item = {
				name: this.itemName,
				price: formattedPrice,
				description: this.description,
				tax_rate: this.taxRate || undefined,
				category: this.category,
				menu_id: this.menu?.id ? this.menu.id : null,
				section_id: this.section?.id ? this.section.id : null
			} as MenuItem;

			await this.addOpenItemToCart({
				item,
				quantity: this.quantity,
				saveOpenItem: this.saveOpenItem
			});

			this.$emit('item-added');
			this.$emit('close');
		}
		catch (errorMessage) {
			Toast.error(this.$t('orders.open_item.error_adding_item', { errorMessage }));
		}
		finally {
			this.loading = false;
		}
	}

	/**
	 * Reset all form fields back to their default values
	 *
	 * @return {void}
	 */
	private resetForm(): void {
		this.selectedItem = '';
		this.price = '';
		this.description = '';
		this.taxRate = null;
		this.category = '';
		this.quantity = 1;
		this.saveOpenItem = false;
		this.setDefaultMenuAndSection();
	}

	/**
	 * Set the uncategorized menu and section as the default selections.
	 * The uncategorized menu is always the first index of operatorMenus.
	 *
	 * @return {void}
	 */
	private setDefaultMenuAndSection(): void {
		this.menu = this.operatorMenus[0];
		const sections = this.menu.sections || [];
		this.section = sections.find((s: MenuSection) => s.name === CATERING_OPEN_ITEM.SECTION_NAME) || sections[0] || null;
	}

	private formatItemPrice = (price: number) => formatValue(price, '', 'price');

}
